import { Autocomplete } from 'components/ui/Autocomplete/Autocomplete';
import type { CountryForSelect } from 'models';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { getRecipientCountriesForSelect } from 'selectors';
import type { RootState } from '../../../../reducers/types';
import type { FieldInterface, FieldValueType } from '../Field';

export interface CountryFieldInterface extends FieldInterface {
  value: Array<FieldValueType>;
}

type CountryFieldProps = {
  countries: CountryForSelect[];
  field: CountryFieldInterface;
  handleBlur: () => void;
  handleChange: (name: string, value: string) => void;
};

const EMPTY_VALUE = '';

export const CountryFieldComponent = ({
  countries,
  field,
  handleBlur,
  handleChange,
}: CountryFieldProps) => {
  const { defaultValue, error, help, label, id, readOnly, required } = field;

  const dataTestID = `data-testid-${id}`;
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [readOnlyState, setReadOnlyState] = useState(!!readOnly);

  const autocompleteHandleChange = (value: string) => {
    setSelectedValue(value);
    handleChange(id, value);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally run only on mount to set initial value
  useEffect(() => {
    if (!defaultValue) {
      setReadOnlyState(false);
      return;
    }

    const found = countries.find((country: Record<string, string>) => {
      if (country.value !== defaultValue) {
        return false;
      }

      autocompleteHandleChange(defaultValue);
      return true;
    });

    if (!found) {
      setReadOnlyState(false);
      autocompleteHandleChange(EMPTY_VALUE);
    }
  }, []);

  return (
    <Autocomplete
      name={id}
      onChange={autocompleteHandleChange}
      onBlur={handleBlur}
      options={countries}
      data-testid={dataTestID}
      selected={selectedValue}
      error={error}
      hint={help}
      required={!!required}
      readOnly={readOnlyState}
    >
      <div>
        <Autocomplete.Input data-testid={dataTestID} placeholder={label} />
        <Autocomplete.Options />
      </div>
      {help && (
        <Autocomplete.Hint className="FormGroup-hint">{help}</Autocomplete.Hint>
      )}
      {error && <Autocomplete.Error>{error}</Autocomplete.Error>}
    </Autocomplete>
  );
};

CountryFieldComponent.propTypes = {
  countries: PropTypes.array,
  field: PropTypes.object,
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func,
};

const mapStateToProps = (state: RootState) => ({
  countries: getRecipientCountriesForSelect(state),
});

export const CountryField = connect(mapStateToProps)(CountryFieldComponent);
